import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api';

const LabContext = createContext(null);

const STORAGE_KEY = 'selected_lab_id';

// localStorage'dan xavfsiz o'qish — faqat sof son yoki null qaytaradi.
// Boshqa qiymatlar ("all", "abc", bo'sh) → null ("Hammasi" rejimi).
function readSaved() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (!v) return null;
    if (!/^\d+$/.test(v)) return null;
    return parseInt(v, 10);
  } catch { return null; }
}

function writeSaved(id) {
  try {
    if (id == null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(id));
  } catch {}
}

export function LabProvider({ children }) {
  const [labs, setLabs] = useState([]);
  // null = "Hammasi" (2+ klubli egada standart). Son = aniq klub.
  const [labId, setLabIdState] = useState(() => readSaved());
  const [loading, setLoading] = useState(true);

  const refreshLabs = useCallback(async () => {
    try {
      const r = await api.get('/owner/labs');
      const list = r.data || [];
      setLabs(list);

      // Standart tanlov qoidasi:
      // - 0 klub: labId=null (hech narsa yo'q)
      // - 1 klub: labId=labs[0].id (dropdown ko'rinmaydi, lekin backend uchun aniq klub)
      // - 2+ klub: saqlangan qiymat valid bo'lsa — qoldiramiz; aks holda null ("Hammasi")
      if (list.length === 0) {
        if (labId !== null) {
          setLabIdState(null);
          writeSaved(null);
        }
      } else if (list.length === 1) {
        // 1 klub — "Hammasi" semantikasi yo'q, doim o'sha klubga tiramiz
        if (labId !== list[0].id) {
          setLabIdState(list[0].id);
          writeSaved(list[0].id);
        }
      } else {
        // 2+ klub — saqlangan qiymat endi egaga tegishli emasmi?
        if (labId !== null && !list.some(l => l.id === labId)) {
          setLabIdState(null);
          writeSaved(null);
        }
        // labId=null bo'lsa — "Hammasi" standart, teginmaymiz
      }
    } catch {}
  }, [labId]);

  // Owner barcha filiallarini yuklash
  useEffect(() => {
    refreshLabs().finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setLabId = useCallback((id) => {
    // id === null yoki id === '' yoki "all" → "Hammasi"
    const next = (id == null || id === '' || id === 'all') ? null : parseInt(id, 10);
    setLabIdState(next);
    writeSaved(next);
  }, []);

  const currentLab = labs.find(l => l.id === labId) || null;
  // 'all' | 'single' | 'none' — UI uchun qulay tahlil
  const mode = labs.length === 0 ? 'none'
             : labs.length === 1 ? 'single'
             : labId == null ? 'all'
             : 'single';

  return (
    <LabContext.Provider value={{ labs, labId, setLabId, currentLab, mode, loading, refreshLabs }}>
      {children}
    </LabContext.Provider>
  );
}

export function useLab() {
  const ctx = useContext(LabContext);
  if (!ctx) throw new Error('useLab must be used within LabProvider');
  return ctx;
}
